import React, { useState, useRef, useEffect } from 'react';
import { FiEye, FiChevronDown } from 'react-icons/fi';
import {
  useRolePreview,
  PREVIEWABLE_ROLES,
  PreviewableRole,
} from '../../contexts/RolePreviewContext';

/** Navbar control: superadmin-only “Preview as…” dropdown. */
const RolePreviewControl: React.FC = () => {
  const { canUseRolePreview, previewRole, isPreviewing, setPreviewRole, clearPreview } =
    useRolePreview();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!canUseRolePreview) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Preview the console as another role (GUI only)"
        className={`hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-colors ${
          isPreviewing
            ? 'border-amber-300 bg-amber-400/20 text-white'
            : 'border-white/25 hover:border-white/50 hover:bg-white/10 text-white'
        }`}
      >
        <FiEye className="text-xs" />
        {isPreviewing ? `As ${previewRole}` : 'Preview role'}
        <FiChevronDown className="text-xs opacity-75" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-surface border border-border-subtle shadow-md rounded z-50 py-1">
          <div className="px-3 py-2 text-xs text-ink-secondary border-b border-border-subtle">
            GUI only — API stays superadmin
          </div>
          <button
            type="button"
            className="block w-full text-left px-3 py-2 text-sm text-ink-primary hover:bg-surface-raised"
            onClick={() => {
              clearPreview();
              setOpen(false);
            }}
          >
            Own role (superadmin)
          </button>
          {PREVIEWABLE_ROLES.filter((r) => r !== 'superadmin').map((r) => (
            <button
              key={r}
              type="button"
              className={`block w-full text-left px-3 py-2 text-sm hover:bg-surface-raised ${
                previewRole === r ? 'font-semibold text-accent' : 'text-ink-primary'
              }`}
              onClick={() => {
                setPreviewRole(r as PreviewableRole);
                setOpen(false);
              }}
            >
              {r}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default RolePreviewControl;
